import React, { useState } from "react";
import moment from "moment";
import { Box, Stack, Typography } from "@mui/material";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import ButtonField from "../../Ui/ButtonField";
import DialogLayout from "../../DialogLayout";
import ContactSupportForm from "../Support/ContactSupportForm";
import { getSupportContact, SUPPORT_SUBJECT_COURSE_ISSUE } from "../../../config/supportContact";
import { getActivePrestataireBrandConfig, getActivePrestationBrandConfig } from "../../../config/environment.config";
import { capitalizeFirstLetter } from "../../../Utils/Function";
import { useTailwindBrand } from "../../shared";

interface CourseReportCtaProps {
    /** Cours tel que renvoyé par l'API du tableau de bord client. */
    course: {
        id?: number;
        startDatetime?: string;
        endDatetime?: string;
        isPunctualMonth?: boolean;
        subjectAsked?: string;
        teachrFirstName?: string | null;
        teachrLastName?: string | null;
    };
}

/**
 * Résumé du cours transmis à l'équipe avec le message, et repris dans l'objet
 * du mail : « Cours n°123 du 12/10/2026 (Mathématiques) avec Jean Dupont ».
 */
export const describeCourseForSupport = (course: CourseReportCtaProps["course"]): string => {
    const start = course.startDatetime ? moment(course.startDatetime).format("DD/MM/YYYY") : null;
    const end = course.endDatetime ? moment(course.endDatetime).format("DD/MM/YYYY") : null;
    const period = start === null
        ? ""
        : course.isPunctualMonth && end !== null && end !== start
            ? ` du ${start} au ${end}`
            : ` du ${start}`;
    const teachrName = [course.teachrFirstName, course.teachrLastName].filter(Boolean).join(" ");
    const subject = course.subjectAsked ? ` (${capitalizeFirstLetter(course.subjectAsked)})` : "";
    const withTeachr = teachrName ? ` avec ${capitalizeFirstLetter(teachrName)}` : "";
    return `${capitalizeFirstLetter(getActivePrestationBrandConfig())} n°${course.id ?? "?"}${period}${subject}${withTeachr}`;
};

/**
 * Détail d'un cours côté client : « Un problème avec ce cours ? ». Trois
 * façons de nous joindre : un message depuis l'application (formulaire de
 * contact, cours pré-rempli), un mail (objet pré-rempli) ou un appel si le
 * numéro de la marque est renseigné.
 */
const CourseReportCta: React.FC<CourseReportCtaProps> = ({ course }) => {
    const { brandConfig } = useTailwindBrand();
    const contact = getSupportContact();
    const [open, setOpen] = useState(false);
    const primary = brandConfig.colors.primary;
    const ff = brandConfig.fontFamily;
    const context = describeCourseForSupport(course);
    const mailHref = `mailto:${contact.email}?subject=${encodeURIComponent(`${SUPPORT_SUBJECT_COURSE_ISSUE} : ${context}`)}`;

    return (
        <>
            <Box
                sx={{
                    mt: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: `${primary}33`,
                    bgcolor: `${primary}08`,
                    px: { xs: 2, md: 2.5 },
                    py: 2,
                }}
            >
                <Typography variant="subtitle2" fontWeight={700} sx={{ fontFamily: ff, color: primary, lineHeight: 1.2, mb: 0.5 }}>
                    🤝 Un problème avec {getActivePrestationBrandConfig("", "ce ", "cette ")} ?
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: ff, color: brandConfig.colors.text, mb: 1.5 }}>
                    Une date, une durée ou un montant qui ne correspond pas à ce que vous avez convenu avec votre {getActivePrestataireBrandConfig()} ?
                    Signalez-le nous, nous vérifions avec lui et revenons vers vous rapidement.
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap flexWrap="wrap">
                    <ButtonField
                        label="Nous envoyer un message"
                        variant="contained"
                        size="small"
                        startIcon={<ChatBubbleOutlineRoundedIcon fontSize="small" />}
                        onClick={() => setOpen(true)}
                    />
                    <ButtonField
                        label="Nous écrire par mail"
                        variant="outlined"
                        size="small"
                        startIcon={<MailOutlineRoundedIcon fontSize="small" />}
                        onClick={() => { window.location.href = mailHref; }}
                    />
                    {contact.phone && (
                        <ButtonField
                            label={`Nous appeler : ${contact.phoneDisplay ?? contact.phone}`}
                            variant="outlined"
                            size="small"
                            startIcon={<PhoneInTalkOutlinedIcon fontSize="small" />}
                            onClick={() => { window.location.href = `tel:${contact.phone}`; }}
                        />
                    )}
                </Stack>
                {contact.phone && (
                    <Typography variant="caption" sx={{ fontFamily: ff, color: "text.secondary", display: "block", mt: 1 }}>
                        Par téléphone {contact.hours}.
                    </Typography>
                )}
            </Box>

            <DialogLayout
                title={`Signaler un problème sur ${getActivePrestationBrandConfig("", "ce ", "cette ")}`}
                text={<p>{context}</p>}
                open={open}
                maxWidth="md"
                handleClose={() => setOpen(false)}
                handleCloseIcon={() => setOpen(false)}
                customDialogActions="hidden"
            >
                <ContactSupportForm
                    defaultSubject={SUPPORT_SUBJECT_COURSE_ISSUE}
                    context={context}
                    intro="Décrivez ce qui ne correspond pas : nous vérifions avec votre interlocuteur et revenons vers vous."
                    onSent={() => setOpen(false)}
                />
            </DialogLayout>
        </>
    );
};

export default CourseReportCta;
